/**
 * Sidebar — navegação lateral do dashboard.
 * Ícones SVG inline (stroke 1.8, consistentes) em vez de emojis — alinhado
 * com o design system (no-emoji-icons / vector-only / stroke-consistency).
 */
import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMinhasEmpresas } from '../hooks/useMinhasEmpresas';
import { useModoRecrutamento } from '../hooks/useModoRecrutamento';
import { filtrarNavegacao } from '../utils/modoRecrutamento';

const Icon = ({ d, paths }) => (
  <svg
    width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }} aria-hidden="true"
  >
    {paths || <path d={d} />}
  </svg>
);

const I = {
  home:   <Icon paths={<><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></>} />,
  pin:    <Icon paths={<><path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>} />,
  cal:    <Icon paths={<><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></>} />,
  scale:  <Icon paths={<><path d="M12 3v18M7 21h10M5 7h14M5 7l-2.5 6a3 3 0 0 0 5 0L5 7Zm14 0-2.5 6a3 3 0 0 0 5 0L19 7Z" /></>} />,
  bell:   <Icon paths={<><path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" /><path d="M10 19a2 2 0 0 0 4 0" /></>} />,
  panel:  <Icon paths={<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></>} />,
  search: <Icon paths={<><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>} />,
  inbox:  <Icon paths={<><path d="M4 5h16v14H4z" /><path d="M4 14h4l2 3h4l2-3h4" /></>} />,
  house:  <Icon paths={<><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5M9 21v-7h6v7" /></>} />,
  crown:  <Icon paths={<><path d="m3 7 4.5 4L12 4l4.5 7L21 7l-2 11H5L3 7Z" /><path d="M5 21h14" /></>} />,
  briefcase: <Icon paths={<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V4h8v3M3 12h18" /></>} />,
  logout: <Icon paths={<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></>} />,
};

const NAV_ITEMS_CLIENT = [
  { to: '/dashboard',             label: 'Início',          icon: I.home },
  { to: '/dashboard?s=services',  label: 'Explorar',        icon: I.search, section: 'services' },
  { to: '/dashboard?s=requests',  label: 'Meus pedidos',    icon: I.inbox, section: 'requests' },
  { to: '/minha-casa',            label: 'Minha Casa',      icon: I.house },
  { to: '/nearby-workers', label: 'Profissionais próximos', icon: I.pin },
  { to: '/appointments',   label: 'Agendamentos',           icon: I.cal },
  { to: '/disputes',       label: 'Mediações',               icon: I.scale },
  { to: '/notifications',  label: 'Notificações',           icon: I.bell },
  { to: '/premium',        label: 'Dozy Premium',            icon: I.crown, product: 'premium' },
  // Só entra na lista quando a pessoa já tem empresa. Sem isso, "Dozy
  // Business" era uma porta para criar empresa no meio do painel de cliente.
  { to: '/empresa',        label: 'Área da empresa',         icon: I.briefcase, product: 'business', soComEmpresa: true },
];

const NAV_ITEMS_WORKER = [
  { to: '/worker',         label: 'Painel',         icon: I.panel },
  { to: '/notifications',  label: 'Notificações',   icon: I.bell },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { temEmpresa } = useMinhasEmpresas();
  const { activo: recrutamento } = useModoRecrutamento();
  const base = user?.role === 'worker' ? NAV_ITEMS_WORKER : NAV_ITEMS_CLIENT;
  const items = filtrarNavegacao(
    base.filter((item) => !item.soComEmpresa || temEmpresa),
    user?.role === 'worker' ? false : recrutamento,
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="sidebar">
      <h2 className="sidebar-brand">Dozy<span>.</span></h2>
      <nav className="sidebar-nav">
        {items.map(({ to, label, icon, section, product }, idx) => (
          <React.Fragment key={to}>
          {product && !items[idx - 1]?.product && <div className="sidebar-sep" role="separator" />}
          <NavLink
            to={to}
            end
            className={({ isActive }) => {
              const actual = new URLSearchParams(location.search).get('s');
              const estaActivo = section
                ? location.pathname === '/dashboard' && actual === section
                : isActive && !(to === '/dashboard' && actual);
              return `nav-link${estaActivo ? ' active' : ''}${product ? ` nav-link--${product}` : ''}`;
            }}
          >
            {icon}
            <span>{label}</span>
          </NavLink>
          </React.Fragment>
        ))}
      </nav>
      <button onClick={handleLogout} className="sidebar-logout">
        {I.logout}
        <span>Sair</span>
      </button>
    </div>
  );
};
